import { AnimatePresence } from "framer-motion";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { DataContext } from "../../context/DataContext";
import { TradeTypeEnum, useAccount } from "../../hooks/api/admin/useAccount";
import { Positions } from "../Positions";
import { AccountSettings } from "../Settings/AccountSettings";
import { Spinner } from "../shared/Spinner";
import { AccountStats } from "./stats/AccountStats";
import { MMPStats } from "./stats/MMPStats";
import { MarginStats } from "./stats/MarginStats";
import { AccountContentWrapper, AccountStatWrapper, LeftSide, RightSide, TabWrapper } from "./style";
import { TradeHistoryTable } from "./tables/TradeHistoryTable";
import { Orders } from "../Orders";
import { useAccountTransactions } from "../../hooks/api/admin/useAccountTransactions";
import { useAccountDegenTransactions } from "../../hooks/api/admin/useAccountDegenTransactions";
import { TxHistoryTable } from "./tables/TxHistoryTable";
import { ISelectOption, Select } from "../shared/Select";
import { COMPONENTS } from "../../constants/design/spacing";
import { useAccountStrategies } from "../../hooks/api/admin/useAccountStrategies";
import { StrategiesTable } from "./tables/StrategiesTable";
import { PositionsWrapper } from "../Positions/style";
import { EpochRewardsTable } from "./tables/EpochRewardsTable";
import { STRATEGY_ADDRESSES_TO_NAME } from "../../constants/strategies";
import { DEGEN_ACCOUNT_ADDRESS } from "../../constants/admin";
import { IsolatedHealth } from "./IsolatedHealth";

enum TabsEnum {
  POSITIONS = "positions",
  ISOLATED_HEALTH = "isolated-health",
  TRADES = "trades",
  TRANSACTIONS = "transactions",
  ORDERS = "orders",
  STRATEGIES = "strategies",
  SETTINGS = "settings",
}

export function Account() {
  const [tradeTypes, setTradeTypes] = useState<Set<TradeTypeEnum>>(
    new Set<TradeTypeEnum>([TradeTypeEnum.TRADE, TradeTypeEnum.LIQUIDATION])
  );

  const { account, tab } = useParams<{ account: string; tab: string }>();
  const isDegenAccount = account?.toLowerCase() === DEGEN_ACCOUNT_ADDRESS.toLowerCase();

  const { data: accountTxsData, isLoading: accountTxsLoading } = useAccountTransactions();
  const { data: accountData, isLoading: isAccountLoading } = useAccount(undefined, Array.from(tradeTypes));
  const { data: strategiesData, isLoading: strategiesLoading } = useAccountStrategies();
  const { data: degenTxsData, isLoading: degenTxsLoading } = useAccountDegenTransactions();
  const { setAddress, records } = useContext(DataContext);

  const navigate = useNavigate();
  const [activeTab, setTab] = useState<string>(TabsEnum.POSITIONS);

  const onSetTab = useCallback(
    (t: string) => {
      setTab(t);
      navigate(`/account/${account}/${t}`);
    },
    [account, navigate]
  );

  useEffect(() => {
    if (account) setAddress(account);
    if (tab && Object.values(TabsEnum).includes(tab as TabsEnum)) {
      // URL already has a valid tab — just sync local state, don't push
      // another history entry for the URL we're already on.
      setTab(tab);
    } else if (account) {
      // Default to Positions when no tab in URL. Use `replace` so pressing
      // Back from /account/<addr>/positions returns to the dashboard
      // instead of looping back through /account/<addr> → effect re-fires
      // → /positions, which would trap the user on this page.
      navigate(`/account/${account}/${TabsEnum.POSITIONS}`, { replace: true });
    }
  }, [account, navigate, setAddress, tab]);

  // Transform and merge degen transactions for the specific account
  const mergedTransactions = useMemo(() => {
    const regularTxs = accountTxsData?.tx_history.transaction_history || [];

    if (!isDegenAccount || !degenTxsData?.data) {
      return regularTxs;
    }

    // Transform degen transactions to IAevoTransaction format (limit to 5000)
    const transformedDegenTxs = degenTxsData.data.slice(0, 3000).map((degenTx) => ({
      account: account || "",
      amount: (parseFloat(degenTx.Amount) * 1e6).toString(), // Convert to smallest unit
      counter_party: "", // Leave blank for degen transactions
      collateral: degenTx.Collateral,
      collateral_name: degenTx.CollateralName,
      // eslint-disable-next-line no-underscore-dangle
      finalized_timestamp: degenTx._CreatedTimestamp,
      // eslint-disable-next-line no-underscore-dangle
      initiated_timestamp: degenTx._CreatedTimestamp,
      l1_tx_hash: degenTx.OriginTxHash || "",
      l2_tx_hash: degenTx.L2TxHash,
      tx_status: (degenTx.Status === "finalized" ? "finalized" : "initiated") as "finalized" | "initiated",
      tx_type: degenTx.TxType as "deposit" | "withdraw" | "send" | "receive",
      chain_id: Number(degenTx.ChainId),
      decimals: 6, // USDC decimals
      transfer_details: {
        description: "",
        link: "",
      },
      label: "DEGEN TX", // Marker for degen transactions
    }));

    // Merge and sort by finalized timestamp
    const merged = [...regularTxs, ...transformedDegenTxs];
    merged.sort((a, b) => Number(b.finalized_timestamp) - Number(a.finalized_timestamp));

    return merged;
  }, [accountTxsData, degenTxsData, isDegenAccount, account]);

  const tabs: ISelectOption[] = useMemo(
    () =>
      Object.values(TabsEnum).map((t) => ({
        label: t === TabsEnum.ISOLATED_HEALTH ? "isolated health" : t,
        isActive: t === activeTab,
        onClick: () => onSetTab(t),
      })),
    [activeTab, onSetTab]
  );

  return (
    <div>
      <AnimatePresence>
        <AccountContentWrapper>
          <TabWrapper>
            <Select options={tabs} />
          </TabWrapper>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 420px",
              height: `calc(100vh - 52px - ${COMPONENTS.header.main}px)`,
            }}
          >
            <LeftSide>
              {activeTab === TabsEnum.POSITIONS ? <Positions /> : null}
              {activeTab === TabsEnum.ISOLATED_HEALTH ? <IsolatedHealth /> : null}
              {activeTab === TabsEnum.TRANSACTIONS ? (
                <TxHistoryTable
                  data={mergedTransactions}
                  records={records}
                  isLoading={accountTxsLoading || (isDegenAccount && degenTxsLoading)}
                />
              ) : null}
              {activeTab === TabsEnum.TRADES ? (
                <TradeHistoryTable
                  tradeTypes={tradeTypes}
                  setTradeTypes={setTradeTypes}
                />
              ) : null}
              {activeTab === TabsEnum.ORDERS ? <Orders /> : null}
              {activeTab === TabsEnum.STRATEGIES ? (
                <PositionsWrapper>
                  <StrategiesTable
                    strategy_positions={strategiesData?.strategy_positions}
                    isLoading={strategiesLoading}
                    name="Strategies"
                  />

                  {strategiesData?.strategy_rewards &&
                    Object.values(strategiesData.strategy_rewards).map((rewards) => (
                      <EpochRewardsTable
                        key={rewards.strategy_address}
                        rewards={rewards}
                        isLoading={strategiesLoading}
                        name={`${STRATEGY_ADDRESSES_TO_NAME[rewards.strategy_address]} Epoch Rewards`}
                      />
                    ))}
                </PositionsWrapper>
              ) : null}
              {activeTab === TabsEnum.SETTINGS ? <AccountSettings /> : null}
            </LeftSide>
            <RightSide>
              {
                // eslint-disable-next-line no-nested-ternary
                !accountData || isAccountLoading ? (
                  <Spinner />
                ) : (
                  <AccountStatWrapper>
                    <AccountStats data={accountData} />
                    <MMPStats data={accountData} />
                    <MarginStats accountData={accountData} />
                  </AccountStatWrapper>
                )
              }
            </RightSide>
          </div>
        </AccountContentWrapper>
      </AnimatePresence>
    </div>
  );
}
